import 'bootstrap/dist/css/bootstrap.min.css'
import 'css/App.css'
import 'css/bootstrapExtends.css'
import { Route, Routes } from 'react-router-dom'
import ScrollToTop from 'components/ScrollToTop'
import ATEMQuestDealPurchaseComplete from 'scenes/ATEMQuestDealPurchaseComplete'
import BpoProcessingScreen from 'scenes/BpoProcessingScreen'
import CampaignDone from 'scenes/CampaignDone'
import CampaignQrExpired from 'scenes/CampaignQrExpired'
import CampaignVerifyHandler from 'scenes/CampaignVerifyHandler'
import BpoRejectedScreen from 'scenes/BpoRejectedScreen'
import ErrorResponseScreen from 'scenes/ErrorResponseScreen'
import ATEMQuestDealPurchaseForBankTransfer from 'scenes/ATEMQuestDealPurchaseForBankTransfer'
import ATEMQuestDealPurchaseForCreditCard from 'scenes/ATEMQuestDealPurchaseForCreditCard'
import ATEMQuestDealPurchaseForCreditCardConfirm from 'scenes/ATEMQuestDealPurchaseForCreditCardConfirm'
import ConnectorEdit from 'scenes/ConnectorEdit'
import ConnectorEditConfirm from 'scenes/ConnectorEditConfirm'
import ConnectorSubscription from 'scenes/ConnectorSubscription'
import ConnectorSubscriptionConfirm from 'scenes/ConnectorSubscriptionConfirm'
import ContactUs from 'scenes/ContactUs'
import ContactUsComplete from 'scenes/ContactUsComplete'
import DocumentDownload from 'scenes/DocumentDownload'
import EmailSentComplete from 'scenes/EmailSentComplete'
import Login from 'scenes/Login'
import MyPage from 'scenes/MyPage'
import NotFound from 'scenes/NotFound'
import NotificationDetail from 'scenes/NotificationDetail'
import NotificationList from 'scenes/NotificationList'
import OrganizationManagement from 'scenes/OrganizationManagement'
import PasswordEdit from 'scenes/PasswordEdit'
import PasswordEditConfirm from 'scenes/PasswordEditConfirm'
import PasswordRegistration from 'scenes/PasswordRegistration'
import PasswordReset from 'scenes/PasswordReset'
import PasswordResetExpired from 'scenes/PasswordResetExpired'
import PasswordResetUsed from 'scenes/PasswordResetUsed'
import PaymentSelect from 'scenes/PaymentSelect'
import PositionContract from 'scenes/PositionContract'
import PositionManagement from 'scenes/PositionManagement'
import PositionSelect from 'scenes/PositionSelect'
import ReferrerManagementBinaryMap from 'scenes/ReferrerManagementBinaryMap'
import ReferrerManagementPersonalGroupList from 'scenes/ReferrerManagementPersonalGroupList'
import RegistrationExpired from 'scenes/RegistrationExpired'
import ReferrerManagementUniLevelList from 'scenes/ReferrerManagementUniLevelList'
import RegisterIdentificationForCompany from 'scenes/RegisterIdentificationForCompany'
import RegisterIdentificationForCompanyComplete from 'scenes/RegisterIdentificationForCompanyComplete'
import RegisterIdentificationForIndividual from 'scenes/RegisterIdentificationForIndividual'
import IdentificationQualityError from 'scenes/IdentificationQualityError'
import RewardSlip from 'scenes/RewardSlip'
import SettlementList from 'scenes/SettlementList'
import ShowWithdrawal from 'scenes/ShowWithdrawal'
import SignUpIntroduction from 'scenes/SignUpIntroduction'
import CampaignSignUpComplete from 'scenes/CampaignSignUpComplete'
import CampaignSignUp from 'scenes/CampaignSignUp'
import CampaignSignUpConfirm from 'scenes/CampaignSignUpConfirm'
import SignUpPreRegisterConnector from 'scenes/SignUpPreRegisterConnector'
import SignUpPreRegisterConnectorComplete from 'scenes/SignUpPreRegisterConnectorComplete'
import SignUpPreRegisterConnectorConfirm from 'scenes/SignUpPreRegisterConnectorConfirm'
import SummaryElectronic from 'scenes/SummaryElectronic'
import SummaryPaper from 'scenes/SummaryPaper'
import SummarySelect from 'scenes/SummarySelect'
import Top from 'scenes/Top'
import WithdrawalHistories from 'scenes/WithdrawalHistories'
import MaintenanceMode from 'scenes/MaintenanceMode'
import ConditionCheck from 'scenes/ConditionCheck'
import InviteUrlInvalid from 'scenes/InviteUrlInvalid'
import {
  ROUTE_CAMPAIGN_SIGN_UP_PATTERN,
  ROUTE_CAMPAIGN_SIGN_UP_CONFIRM_PATTERN,
  ROUTE_CAMPAIGN_SIGN_UP_COMPLETE,
  ROUTE_CAMPAIGN_SIGN_UP_VERIFY_PATTERN,
  ROUTE_CAMPAIGN_DONE_PATTERN,
  ROUTE_CAMPAIGN_QR_EXPIRED,
  ROUTE_REGISTRATION_EXPIRED,
  ROUTE_INVITE_URL_INVALID,
  ROUTE_QR_INSPECT_ENTRY_PATTERN,
  ROUTE_SUMMARY_SELECT_FLOW_PATTERN,
} from 'constants/routes'
import axios from 'axios'
import { useEffect, useState } from 'react'

// 外部 IP 確認エンドポイント。SPA boot をブロックする可能性があるため
// IPIFY_TIMEOUT_MS を超過したら fail-safe で未許可扱いに倒す。
const IPIFY_URL = 'https://api.ipify.org?format=json'
const IPIFY_TIMEOUT_MS = 3000

type IpifyResponse = { ip: string }

/**
 * クライアント IP を ipify から取得し allowedIPs と突合する。
 * 外部 CDN がハングしても IPIFY_TIMEOUT_MS で未許可扱いに倒すため、
 * SPA boot が永続ブロックされない。
 *
 * @returns useEffect 用の cleanup 関数 (タイマー解放)
 */
const checkIpAllowance = (allowedIPs: string[], setAllowed: (allowed: boolean) => void): (() => void) => {
  let resolved = false
  const timeoutId = window.setTimeout(() => {
    if (resolved) return
    resolved = true
    setAllowed(false)
  }, IPIFY_TIMEOUT_MS)

  axios
    .get<IpifyResponse>(IPIFY_URL, { timeout: IPIFY_TIMEOUT_MS })
    .then((response) => {
      if (resolved) return
      resolved = true
      window.clearTimeout(timeoutId)
      setAllowed(allowedIPs.includes(response.data.ip))
    })
    .catch(() => {
      if (resolved) return
      resolved = true
      window.clearTimeout(timeoutId)
      setAllowed(false)
    })

  return () => window.clearTimeout(timeoutId)
}

function App() {
  // 初期値を null にして、判定が完了するまでレンダリングを抑止する
  const [isAllowed, setIsAllowed] = useState<boolean | null>(null)
  const maintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === 'true'

  useEffect(() => {
    const allowedIPs = process.env.REACT_APP_ALLOWED_IP_ADDRESS?.split(',') || []
    return checkIpAllowance(allowedIPs, setIsAllowed)
  }, [])

  // IP 判定中の場合は何もレンダリングしない
  if (isAllowed === null) {
    return null
  }

  // IP が許可されておらず、かつメンテナンスモードの場合はメンテナンス画面を表示
  if (!isAllowed && maintenanceMode) {
    return (
      <div className="App">
        <MaintenanceMode />
      </div>
    )
  }

  return (
    <div className="App">
      <ScrollToTop />
      <AppRoutes />
    </div>
  )
}

export const AppRoutes = () => (
  <Routes>
    <Route path="/" element={<Top />} />
    <Route path="/aubeQuestDealPurchaseComplete" element={<ATEMQuestDealPurchaseComplete />} />
    <Route path="/aubeQuestDealPurchaseForBankTransfer" element={<ATEMQuestDealPurchaseForBankTransfer />} />
    <Route path="/aubeQuestDealPurchaseForCreditCard" element={<ATEMQuestDealPurchaseForCreditCard />} />
    <Route path="/aubeQuestDealPurchaseForCreditCardConfirm" element={<ATEMQuestDealPurchaseForCreditCardConfirm />} />
    <Route path="/connectorEdit" element={<ConnectorEdit />} />
    <Route path="/connectorEditConfirm" element={<ConnectorEditConfirm />} />
    <Route path="/connectorSubscription" element={<ConnectorSubscription />} />
    <Route path="/connectorSubscriptionConfirm" element={<ConnectorSubscriptionConfirm />} />
    <Route path="/contactUs" element={<ContactUs />} />
    <Route path="/contactUsComplete" element={<ContactUsComplete />} />
    <Route path="/documentDownload" element={<DocumentDownload />} />
    <Route path="/emailSentComplete" element={<EmailSentComplete />} />
    <Route path="/login" element={<Login />} />
    <Route path="/myPage" element={<MyPage />} />
    <Route path="/notificationDetail/:noticeId" element={<NotificationDetail />} />
    <Route path="/notificationList" element={<NotificationList />} />
    <Route path="/organizationManagement" element={<OrganizationManagement />} />
    <Route path="/passwordEdit" element={<PasswordEdit />} />
    <Route path="/passwordEditConfirm" element={<PasswordEditConfirm />} />
    <Route path="/passwordRegistration/:token" element={<PasswordRegistration />} />
    <Route path="/passwordReset" element={<PasswordReset />} />
    <Route path="/passwordResetExpired" element={<PasswordResetExpired />} />
    <Route path="/passwordResetUsed" element={<PasswordResetUsed />} />
    <Route path="/paymentSelect" element={<PaymentSelect />} />
    <Route path="/positionContract" element={<PositionContract />} />
    <Route path="/positionManagement" element={<PositionManagement />} />
    <Route path="/positionSelect" element={<PositionSelect />} />
    <Route path="/referrerManagementBinaryMap/:positionNumber/:positionId" element={<ReferrerManagementBinaryMap />} />
    <Route path="/referrerManagementPersonalGroupList/:positionNumber/:atemQuestId/:pagination" element={<ReferrerManagementPersonalGroupList />} />
    <Route path="/referrerManagementUniLevelList/:positionNumber/:positionId/:pagination" element={<ReferrerManagementUniLevelList />} />
    <Route path={ROUTE_REGISTRATION_EXPIRED} element={<RegistrationExpired />} />
    <Route path="/registerIdentificationForCompany" element={<RegisterIdentificationForCompany />} />
    <Route path="/registerIdentificationForCompanyComplete" element={<RegisterIdentificationForCompanyComplete />} />
    <Route path="/registerIdentificationForIndividual" element={<RegisterIdentificationForIndividual />} />
    <Route path="/identification/quality-error" element={<IdentificationQualityError />} />
    <Route path="/bpo/processing" element={<BpoProcessingScreen />} />
    <Route path="/bpo/rejected" element={<BpoRejectedScreen />} />
    <Route path="/bpo/error-response" element={<ErrorResponseScreen />} />
    <Route path="/rewardSlip" element={<RewardSlip />} />
    <Route path="/settlementList" element={<SettlementList />} />
    <Route path="/showWithdrawal" element={<ShowWithdrawal />} />
    <Route path={ROUTE_CAMPAIGN_SIGN_UP_PATTERN} element={<CampaignSignUp />} />
    <Route path={ROUTE_CAMPAIGN_SIGN_UP_CONFIRM_PATTERN} element={<CampaignSignUpConfirm />} />
    <Route path={ROUTE_CAMPAIGN_SIGN_UP_COMPLETE} element={<CampaignSignUpComplete />} />
    <Route path={ROUTE_CAMPAIGN_SIGN_UP_VERIFY_PATTERN} element={<CampaignVerifyHandler />} />
    <Route path={ROUTE_CAMPAIGN_DONE_PATTERN} element={<CampaignDone />} />
    <Route path={ROUTE_CAMPAIGN_QR_EXPIRED} element={<CampaignQrExpired />} />
    <Route path={ROUTE_INVITE_URL_INVALID} element={<InviteUrlInvalid />} />
    <Route path="/signUpPreRegisterConnector/:type/:hash" element={<SignUpPreRegisterConnector />} />
    <Route path="/signUpPreRegisterConnectorComplete" element={<SignUpPreRegisterConnectorComplete />} />
    <Route path="/signUpPreRegisterConnectorConfirm/:type/:hash" element={<SignUpPreRegisterConnectorConfirm />} />
    <Route path="/summaryElectronic/:type/:hash" element={<SummaryElectronic />} />
    <Route path="/summaryPaper/:type/:hash" element={<SummaryPaper />} />
    <Route path={ROUTE_QR_INSPECT_ENTRY_PATTERN} element={<SignUpIntroduction />} />
    <Route path={ROUTE_SUMMARY_SELECT_FLOW_PATTERN} element={<SummarySelect />} />
    <Route path="/withdrawHistories" element={<WithdrawalHistories />} />
    <Route path="/requestWithdrawal" element={<ShowWithdrawal />} />
    <Route path="/maintenance" element={<MaintenanceMode />} />
    <Route path="/conditionCheck" element={<ConditionCheck />} />
    <Route path="*" element={<NotFound />} />
  </Routes>
)

export default App
